import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyPin } from '../../../services/authService';
import { showErrorToast, showSuccessToast } from '../../../utils/toastUtils';
import Cookies from 'js-cookie';
import { ShieldCheck, Smartphone, Lock, X } from 'lucide-react';

const PinInput = ({ inputRefs, onPinChange, onSubmit }) => {
  const [pin, setPin] = useState(['', '', '', '', '', '']);

  const handleChange = (index, value) => {
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    const fullPin = newPin.join('');
    if (onPinChange) {
      onPinChange(fullPin);
    }

    if (value !== '' && index < 5) {
      inputRefs.current[index + 1].current.focus();
    }

    // Auto-submit when 6 digits are entered (Preserved original logic)
    if (fullPin.length === 6 && onSubmit) {
       onSubmit(fullPin);
    }
  };

  const handleBackspace = (index, event) => {
    if (event.key === 'Backspace' && index > 0 && pin[index] === '') {
      inputRefs.current[index - 1].current.focus();
    }
  };

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3">
      {pin.map((value, index) => (
        <React.Fragment key={index}>
          <input
            ref={inputRefs.current[index]}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleBackspace(index, e)}
            className="w-12 h-14 text-center text-xl font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-200 shadow-sm"
            autoFocus={index === 0}
          />
          {index === 2 && (
            <span className="text-slate-400 font-semibold text-lg select-none">&ndash;</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ModalWithPinInput = ({ isModalOpen, toggleModal, tempToken, verificationMethod }) => {
  const inputRefs = useRef(Array.from({ length: 6 }).map(() => React.createRef()));
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const isAuthenticator = verificationMethod === "authenticator_code";

  const handleSubmit = async (currentPin = pin) => {
    if (currentPin.length !== 6) {
      setError(`Please enter a valid 6-digit ${isAuthenticator ? 'authenticator code' : 'PIN'}.`);
      return;
    }

    try {
      const result = await verifyPin(currentPin, tempToken, verificationMethod);
      if (result.success) {
        showSuccessToast(result.message);
        Cookies.set('authToken', result.token); 
        navigate('/admin-dashboard'); 
        toggleModal(); 
      } else {
        showErrorToast(result.message);
      }
    } catch (error) {
      showErrorToast("Server error occurred.");
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden transform transition-all">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-[#130092] to-blue-700 text-white p-6 relative">
          <button 
            onClick={toggleModal}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
              {isAuthenticator ? (
                <Smartphone className="w-6 h-6 text-cyan-300" />
              ) : (
                <Lock className="w-6 h-6 text-cyan-300" />
              )}
            </div>
            <div>
              <h3 className="text-xl font-bold tracking-tight">
                {isAuthenticator ? "Two-Factor Authentication" : "Security Verification"}
              </h3>
              <p className="text-xs text-blue-100 mt-0.5">
                {isAuthenticator 
                  ? "Open your Authenticator app to view the 6-digit code" 
                  : "Enter your 6-digit security PIN to continue"}
              </p>
            </div>
          </div>
        </div>

        {/* Body Content */}
        <div className="p-6 sm:p-8">
          <PinInput inputRefs={inputRefs} onPinChange={setPin} onSubmit={handleSubmit} />
          
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm font-medium text-center animate-shake">
              {error}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            type="button"
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-100 transition-colors focus:outline-none"
            onClick={toggleModal}
          >
            Cancel
          </button>
          
          <button
            type="button"
            className="hidden text-center text-white bg-blue-900 shadow-lg shadow-blue-600/30 bg-gradient-to-r from-blue-600 to-blue-800 font-semibold rounded-xl px-5 py-2.5 text-sm hover:from-blue-700 hover:to-blue-900 transition-all"
            onClick={() => handleSubmit()}
          >
            Verify Code
          </button>
        </div>

      </div>
    </div>
  );
};

export default ModalWithPinInput;